import { useState, useEffect, useRef } from "react";
import { showAlertFavor, showAlertErrorMonto } from "../components/Add/Alerts";
import apiFetch from "../../../../shared/services/apiClient";

/** Redondea a 2 decimales — previene errores de precisión flotante en el backend */
const r2 = (n) => Math.round(Number(n) * 100) / 100;

export function useCxc(receiptMonto, selectedFilial, setCount) {
  const [facturasData, setFacturasData] = useState([]);
  const [restante, setRestante] = useState(0);
  const lastValidatedAbonoRef = useRef({});

  useEffect(() => {
    if (!selectedFilial) return;
    async function fetchTodasLasCxC() {
      const res = await apiFetch(
        `/api/finance/cuentaXCobrar/?filial=${selectedFilial}&ordering=fecha_factura,factura__numero_factura&all=1`,
      );
      const data = await res.json();
      const results = Array.isArray(data) ? data : (data.results ?? []);
      const activas = results
        .map((c) => ({ ...c, abono: 0, favor: 0, checked: false }))
        .filter((c) => r2(c.saldo) !== 0 && c.factura != null) // excluir CxC sin factura (créditos manuales)

        .sort((a, b) => {
          // negativos primero
          if (a.saldo < 0 && b.saldo >= 0) return -1;
          if (a.saldo >= 0 && b.saldo < 0) return 1;

          // luego por fecha (opcional)
          return new Date(a.fecha_factura) - new Date(b.fecha_factura);
        });
      setFacturasData(activas);
      setCount(activas.length);
    }
    fetchTodasLasCxC();
  }, [selectedFilial, setCount]);

  useEffect(() => {
    const totalPos = facturasData.reduce(
      (acc, i) => acc + (i.abono > 0 ? i.abono : 0),
      0,
    );
    const totalNeg = facturasData.reduce(
      (acc, i) => acc + (i.abono < 0 ? Math.abs(i.abono) : 0),
      0,
    );
    setRestante(r2(receiptMonto + totalNeg - totalPos));
  }, [facturasData, receiptMonto]);

  useEffect(() => {
    setFacturasData((prev) =>
      prev.map((inv) => ({ ...inv, abono: 0, favor: 0, checked: false })),
    );
  }, [receiptMonto]);

  // ── Poder adquisitivo total disponible ────────────────────────────────
  function poderAdquisitivo() {
    const creditoNeg = facturasData.reduce(
      (acc, i) => acc + (Number(i.saldo) < 0 ? Math.abs(Number(i.saldo)) : 0),
      0,
    );
    return r2(receiptMonto + creditoNeg);
  }

  function distribute() {
    let remaining = r2(receiptMonto);
    const favorNeg = facturasData.reduce(
      (acc, inv) =>
        inv.abono <= 0 && inv.checked ? acc + Number(inv.saldo) : acc,
      0,
    );
    remaining = r2(remaining + Math.abs(favorNeg || 0));
    if (remaining <= 0) {
      showAlertErrorMonto().fire();
      return;
    }

    const distributed = facturasData.map((inv, index) => {
      if (remaining <= 0) return { ...inv, abono: 0, favor: 0, checked: false };
      const saldo = r2(inv.saldo);
      if (inv.abono < 0) return { ...inv, checked: true };
      if (saldo <= 0) return { ...inv, abono: 0, favor: 0, checked: false };
      let abono = 0,
        favor = 0;
      if (index === facturasData.length - 1) {
        abono = r2(Math.min(saldo, remaining));
        favor = abono < remaining ? r2(Math.abs(saldo - remaining)) : 0;
        abono = r2(remaining);
        remaining = 0;
      } else {
        abono = r2(Math.min(saldo, remaining));
        remaining = r2(remaining - abono);
      }
      if (favor > 0) showAlertFavor(favor).fire();
      return { ...inv, abono, favor, checked: abono > 0 };
    });
    setFacturasData(distributed);
  }

  function parseSaldo(val) {
    return r2(Number(val.toString().replace(/[^0-9.-]/g, "")) || 0);
  }

  function updateAbono(id, rawValue) {
    setFacturasData((prev) =>
      prev.map((inv) => {
        if (inv.id !== id) return inv;
        const saldo = parseSaldo(inv.saldo);
        let value = r2(Number(rawValue) || 0);
        let favor = 0;
        if (saldo < 0) return { ...inv, abono: value, favor: 0 };

        // Calcular el poder adquisitivo total (monto + créditos negativos)
        const creditoNeg = prev.reduce(
          (acc, i) =>
            acc +
            (parseSaldo(i.saldo) < 0 && i.checked
              ? Math.abs(parseSaldo(i.saldo))
              : 0),
          0,
        );

        const totalDisponible = r2(receiptMonto + creditoNeg);

        const totalUsado = prev
          .filter((i) => i.id !== id)
          .reduce((a, i) => a + (i.abono || 0), 0);
        const disponible = r2(Math.max(0, totalDisponible - totalUsado));
        if (value > disponible) value = disponible;
        if (value > saldo) {
          favor = r2(value - saldo);
          value = r2(Math.max(value, saldo));
          showAlertFavor(favor).fire();
        }
        lastValidatedAbonoRef.current[id] = value;
        return { ...inv, abono: value, favor };
      }),
    );
  }

  function updateChecked(id, value) {
    let remaining = restante;
    const distributed = facturasData.map((inv) => {
      if (inv.id !== id) return inv;
      const saldo = parseSaldo(inv.saldo);
      if (saldo < 0 && value)
        return {
          ...inv,
          abono: saldo,
          favor: 0,
          checked: true,
          negative: true,
        };
      if (saldo < 0 && !value)
        return { ...inv, abono: 0, favor: 0, checked: false, negative: true };
      if (!value || remaining <= 0)
        return { ...inv, abono: 0, favor: 0, checked: false, negative: false };

      // Calcular poder adquisitivo incluyendo las negativas ya seleccionadas
      const creditoNeg = facturasData.reduce(
        (acc, i) =>
          acc +
          (parseSaldo(i.saldo) < 0 && i.checked
            ? Math.abs(parseSaldo(i.saldo))
            : 0),
        0,
      );
      const totalPoder = r2(receiptMonto + creditoNeg);
      const yaUsado = facturasData
        .filter((i) => i.id !== id && i.abono > 0)
        .reduce((a, i) => a + i.abono, 0);
      const disponible = r2(Math.max(0, totalPoder - yaUsado));

      if (disponible <= 0)
        return { ...inv, abono: 0, favor: 0, checked: false, negative: false };

      const abono = r2(Math.min(saldo, remaining));
      remaining = r2(remaining - abono);
      return { ...inv, abono, favor: 0, checked: abono > 0, negative: false };
    });
    setRestante(r2(Math.max(0, remaining)));
    setFacturasData(distributed);
  }

  function montoEsValido() {
    if (receiptMonto > 0) return true;
    const tieneNegativaSeleccionada = facturasData.some(
      (f) => f.checked && Number(f.abono) < 0,
    );
    return tieneNegativaSeleccionada;
  }

  // Cuando monto=0 y hay saldo a favor seleccionado, retornar la referencia automática
  const negativaSeleccionada = facturasData.find(f => f.checked && Number(f.abono) < 0);
  const soloSaldoFavor = Number(receiptMonto) === 0 && !!negativaSeleccionada;
  // La referencia automática es el numero_recibo del recibo origen del saldo a favor
  // Ej: 'R-00005' — mismo recibo que generó el crédito, sin nuevo ingreso bancario
  const referenciaAutoSaldo = soloSaldoFavor
    ? (negativaSeleccionada.numero_recibo_origen
        ? `Aplicación saldo a favor — Recibo ${negativaSeleccionada.numero_recibo_origen}`
        : (negativaSeleccionada.concepto || 'Aplicación saldo a favor'))
    : null;

  return {
    facturasData,
    distribute,
    updateAbono,
    updateChecked,
    setFacturasData,
    selectedFilial,
    restante,
    lastValidatedAbonoRef,
    montoEsValido,
    soloSaldoFavor,
    referenciaAutoSaldo,
  };
}
